// ACTA_AGENT_ENTITY_RESOLVER_V1
// ACTA_AGENT_FUZZY_ENTITY_V1

export function normalizeAgentText(
  value = ""
) {
  return String(value)
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toLocaleLowerCase("fr-FR")
    .replace(/[’']/g, " ")
    .replace(
      /[^\p{L}\p{N}\s.-]/gu,
      " "
    )
    .replace(/\s+/g, " ")
    .trim();
}

export function prospectDisplayName(
  prospect
) {
  return (
    `${
      prospect?.firstName
      || prospect?.first_name
      || ""
    } ${
      prospect?.lastName
      || prospect?.last_name
      || ""
    }`.trim()
    || prospect?.company
    || prospect?.email
    || "Contact"
  );
}

export function documentDisplayName(
  document
) {
  return (
    document?.name
    || document?.title
    || document?.filename
    || document?.fileName
    || "Document"
  );
}

function documentBasename(
  value = ""
) {
  const name = String(
    value || ""
  ).trim();

  const lastDot =
    name.lastIndexOf(".");

  if (
    lastDot <= 0
    || lastDot ===
      name.length - 1
  ) {
    return name;
  }

  return name.slice(
    0,
    lastDot
  );
}

export function searchableProspect(
  prospect
) {
  return [
    prospectDisplayName(
      prospect
    ),
    prospect?.company,
    prospect?.email,
    prospect?.phone,
    prospect?.city,
  ]
    .filter(Boolean)
    .join(" ");
}

export function searchableDocument(
  document
) {
  const name =
    documentDisplayName(
      document
    );

  const basename =
    documentBasename(name);

  return [
    name,
    basename !== name
      ? basename
      : null,
    document?.description,
    document?.category,
  ]
    .filter(Boolean)
    .join(" ");
}

function levenshteinDistance(
  left = "",
  right = ""
) {
  const a = String(left);
  const b = String(right);

  if (!a.length) {
    return b.length;
  }

  if (!b.length) {
    return a.length;
  }

  const previous =
    Array.from(
      { length: b.length + 1 },
      (_, index) => index
    );

  for (
    let leftIndex = 1;
    leftIndex <= a.length;
    leftIndex += 1
  ) {
    const current = [
      leftIndex,
    ];

    for (
      let rightIndex = 1;
      rightIndex <= b.length;
      rightIndex += 1
    ) {
      const substitutionCost =
        a[leftIndex - 1] ===
        b[rightIndex - 1]
          ? 0
          : 1;

      current[rightIndex] =
        Math.min(
          current[
            rightIndex - 1
          ] + 1,
          previous[
            rightIndex
          ] + 1,
          previous[
            rightIndex - 1
          ] + substitutionCost
        );
    }

    for (
      let index = 0;
      index < current.length;
      index += 1
    ) {
      previous[index] =
        current[index];
    }
  }

  return previous[b.length];
}

function fuzzyTokenMatch(
  candidateToken,
  commandToken
) {
  if (
    candidateToken.length < 4
    || commandToken.length < 4
  ) {
    return false;
  }

  if (
    Math.abs(
      candidateToken.length
      - commandToken.length
    ) > 1
  ) {
    return false;
  }

  return (
    levenshteinDistance(
      candidateToken,
      commandToken
    ) <= 1
  );
}

function candidateScore(
  command,
  candidate
) {
  const normalizedCommand =
    normalizeAgentText(command);

  const normalizedCandidate =
    normalizeAgentText(
      candidate
    );

  if (
    !normalizedCommand
    || !normalizedCandidate
  ) {
    return 0;
  }

  let score = 0;

  if (
    normalizedCommand.includes(
      normalizedCandidate
    )
  ) {
    score += 100;
  }

  const commandTokens =
    normalizedCommand
      .split(" ")
      .filter(Boolean);

  const candidateTokens =
    normalizedCandidate
      .split(" ")
      .filter(
        (token) =>
          token.length >= 2
      );

  for (
    const token
    of candidateTokens
  ) {
    if (
      commandTokens.includes(
        token
      )
    ) {
      score +=
        token.length >= 5
          ? 12
          : 6;

      continue;
    }

    const fuzzy =
      commandTokens.some(
        (commandToken) =>
          fuzzyTokenMatch(
            token,
            commandToken
          )
      );

    if (fuzzy) {
      score += 10;
    }
  }

  return score;
}

export function rankAgentMatches(
  items,
  command,
  searchable
) {
  return (items || [])
    .map((item) => ({
      item,
      score: candidateScore(
        command,
        searchable(item)
      ),
    }))
    .filter(
      ({ score }) =>
        score > 0
    )
    .sort(
      (left, right) =>
        right.score
        - left.score
    );
}

export function bestAgentMatches(
  ranked
) {
  if (!ranked.length) {
    return [];
  }

  const bestScore =
    ranked[0].score;

  return ranked
    .filter(
      ({ score }) =>
        score >= Math.max(
          bestScore - 12,
          bestScore * 0.8
        )
    )
    .slice(0, 5)
    .map(
      ({ item }) =>
        item
    );
}
